import { useCallback, useEffect, useMemo } from 'react';
import { ALL_TAUGHT_KEYS } from '../data/curriculum';
import { pickBaselinePassage } from '../data/baselinePassages';
import type { BaselineSnapshot, KeyboardLayoutId, RoundResult } from '../types';
import { useRoundInput } from '../lib/useRoundInput';
import Keyboard from './Keyboard';

const MAX_DURATION_MS = 90_000;

interface BaselineTranscriptionScreenProps {
  keyboardLayout: KeyboardLayoutId;
  passageSeed?: number;
  onFinish: (snapshot: BaselineSnapshot) => void;
  onCancel?: () => void;
  /** Kan ikke annulleres (fx gen-test efter tastatur-fix). */
  required?: boolean;
}

export default function BaselineTranscriptionScreen({
  keyboardLayout,
  passageSeed,
  onFinish,
  onCancel,
  required = false,
}: BaselineTranscriptionScreenProps) {
  const passage = useMemo(
    () => pickBaselinePassage(passageSeed),
    [passageSeed],
  );

  const handleRoundFinish = useCallback(
    (r: RoundResult) => {
      onFinish({
        kind: 'transcription',
        wpm: r.wpm,
        accuracy: r.accuracy,
        correctChars: r.correctChars,
        totalKeystrokes: r.totalKeystrokes,
        backspaces: r.backspaces,
        durationMs: r.durationMs,
        takenAt: new Date().toISOString(),
        passageId: passage.id,
      });
    },
    [onFinish, passage.id],
  );

  const input = useRoundInput({
    targetText: passage.text,
    mode: 'challenge',
    roundId: 'baseline-transcription',
    roundTitle: 'Afskriftstest',
    presentationKind: 'standard',
    onFinish: handleRoundFinish,
  });

  const { finish, finished, elapsedMs } = input;
  useEffect(() => {
    if (finished || elapsedMs < MAX_DURATION_MS) return;
    finish(false);
  }, [elapsedMs, finished, finish]);

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
      <header>
        <p className="label">
          {required ? 'Opdater udgangspunkt · afskrift' : 'Udgangspunkt · afskrift'}
        </p>
        <h1 className="mt-1 text-2xl font-semibold text-strong">
          Skriv teksten nedenfor
        </h1>
        <p className="mt-2 text-sm text-muted">
          {required
            ? 'Vi har rettet understøttelse af é og andre tegn (Option på Mac). Tag testen igen så dit udgangspunkt og mål passer.'
            : 'Skriv som du plejer — også hvis du kigger på tastaturet. Teksten på skærmen afspejler, hvordan du skriver mails og dokumenter.'}
        </p>
      </header>

      <div className="grid grid-cols-4 gap-3">
        <Stat label="Tid" value={formatMs(input.elapsedMs)} />
        <Stat label="WPM" value={input.liveWpm.toFixed(0)} />
        <Stat
          label="Nøjagtighed"
          value={`${(input.liveAcc * 100).toFixed(0)}%`}
        />
        <Stat
          label="Tegn"
          value={`${input.cursor} / ${passage.text.length}`}
        />
      </div>

      <TextDisplay
        text={passage.text}
        cursor={input.cursor}
        correctMask={input.correctMask}
      />

      <Keyboard
        layoutId={keyboardLayout}
        nextExpectedKey={input.expectedChar}
        lastPressedKey={input.lastPress?.key}
        lastPressCorrect={input.lastPress?.correct}
        unlockedKeys={ALL_TAUGHT_KEYS}
        hintLevel="none"
        showFingerDot={false}
      />

      <div className="flex justify-between gap-3">
        {onCancel ? (
          <button type="button" onClick={onCancel} className="btn-secondary">
            Annuller
          </button>
        ) : (
          <span />
        )}
        <p className="self-center text-xs text-faint">
          Max {MAX_DURATION_MS / 1000} sek · Escape afslutter
        </p>
      </div>
    </div>
  );
}

function TextDisplay({
  text,
  cursor,
  correctMask,
}: {
  text: string;
  cursor: number;
  correctMask: ReadonlyArray<boolean>;
}) {
  return (
    <div className="rounded-xl bg-zinc-50 p-4 font-mono text-lg leading-relaxed ring-1 ring-zinc-200 dark:bg-zinc-900/60 dark:ring-white/5">
      {text.split('').map((ch, i) => {
        const done = i < cursor;
        const correct = done && correctMask[i];
        const wrong = done && !correctMask[i];
        const active = i === cursor;
        return (
          <span
            key={i}
            className={
              wrong
                ? 'bg-rose-200/80 text-rose-950 dark:bg-rose-500/30 dark:text-rose-100'
                : correct
                  ? 'text-strong'
                  : active
                    ? 'border-b-2 border-indigo-500 text-strong'
                    : 'text-faint'
            }
          >
            {ch}
          </span>
        );
      })}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="surface p-3 text-center">
      <p className="text-xs text-faint">{label}</p>
      <p className="mt-0.5 font-mono text-lg font-semibold text-strong">
        {value}
      </p>
    </div>
  );
}

function formatMs(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}:${String(r).padStart(2, '0')}` : `${r}s`;
}
